import { useCallback, useRef, useState } from 'react';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import {
    Badge,
    BottomSheet,
    BottomSheetFooter,
    Button,
    Cell,
    CellText,
    Checkbox,
    Drop,
    Switch,
    useBreakpoint,
    VSpacingContainer,
} from '@hh.ru/magritte-ui';
import { ListOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import { usePush } from '@hh.ru/redux-spa-middleware';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import Notices from 'Modules/Notices';
import Form from 'src/components/Form';
import translation from 'src/components/translation';
import useOnOffState from 'src/hooks/useOnOffState';
import { useResumeSearchSettingsAnalytics } from 'src/hooks/useResumeSearchSettingsAnalytics';
import { useSelector } from 'src/hooks/useSelector';
import useToggleState from 'src/hooks/useToggleState';
import { SettingName, SettingType } from 'src/models/search/resume/result';
import { UserType } from 'src/models/userType';
import setResumeSearchSettings from 'src/utils/employer/setResumeSearchSettings';

const TrlKeys = {
    settingName: {
        [SettingName.ShowFiltersOnTheRight]: 'resume.search.field.show_filters_on_the_right',
    },
    settingType: {
        [SettingType.LastChangeTime]: 'resume.search.field.last_change_time',
        [SettingType.Name]: 'resume.search.field.name',
        [SettingType.Photo]: 'resume.search.field.photo',
        [SettingType.LastExperience]: 'resume.search.field.last_experience',
        [SettingType.Comments]: 'resume.search.field.comments',
        [SettingType.CommentsExpanded]: 'resume.search.field.comments_expanded',
        [SettingType.Languages]: 'resumeCard.field.languages',
        [SettingType.Area]: 'resume.search.field.area',
        [SettingType.Education]: 'resumeCard.field.education',
        [SettingType.Citizenship]: 'resume.search.field.citizenship',
        [SettingType.Companies]: 'resumeCard.field.experience',
        [SettingType.Relocation]: 'resume.search.field.relocation',
        [SettingType.Certificates]: 'resumeCard.field.certificates',
        [SettingType.ExperienceSum]: 'resume.search.field.experience_sum',
        [SettingType.ProfessionalRole]: 'resume.search.field.professional_role',
        [SettingType.RoleWarring]: 'resume.search.field.roleWarring',
    },
    save: 'Save',
    view: 'resume.search.resumeView.button',
};

const NOTICE_NAME = 'resume_search_view_clicked';

const ResumeView: TranslatedComponent = ({ trls }) => {
    const activeSettings = useSelector((state) => state.resumeSearchResult.settings.activeSettings);
    const availableSettings = useSelector((state) => state.resumeSearchResult.settings.availableSettings);
    const filtersOnTheRight = useSelector((state) => state.resumeSearchResult.settings.filtersOnTheRight);
    const resumeSearchViewClicked = useSelector((state) => state.resumeSearchResult.settings.resumeSearchViewClicked);
    const { pathname, search } = useSelector((state) => state.router.location);
    const userType = useSelector(({ userType }) => userType);
    const isAllowedUser = UserType.Employer === userType || UserType.BackOffice === userType;
    const [showViewBadge, setShowViewBadge] = useState(!resumeSearchViewClicked);
    const [isFetching, setIsFetching] = useState(false);
    const [isDropVisible, showDrop, hideDrop] = useOnOffState(false);
    const [switchActivity, toggleSwitch] = useToggleState(filtersOnTheRight?.value ?? true);
    const formElement = useRef<HTMLFormElement>(null);
    const activatorRef = useRef(null);
    const push = usePush();

    const { isMobile } = useBreakpoint();

    const { sendButtonClickAnalytics, sendElementShownAnalytics } = useResumeSearchSettingsAnalytics();

    const handleDropOpen = useCallback(() => {
        if (!resumeSearchViewClicked) {
            Notices.markAsViewed(NOTICE_NAME);
            setShowViewBadge(false);
        }
        showDrop();
        !isDropVisible && sendButtonClickAnalytics('open');
    }, [isDropVisible, resumeSearchViewClicked, sendButtonClickAnalytics, showDrop]);

    const handleSaveForm = useCallback(async () => {
        if (!formElement.current) {
            return;
        }
        setIsFetching(true);
        try {
            await setResumeSearchSettings(new FormData(formElement.current));
        } catch (e) {
            console.error(e);
            return;
        } finally {
            push(`${pathname}${search}`);
            setIsFetching(false);
            hideDrop();
        }
        sendButtonClickAnalytics('close');
    }, [sendButtonClickAnalytics, push, pathname, search, hideDrop]);

    const handleClose = useCallback(() => {
        hideDrop();
        sendButtonClickAnalytics('close');
    }, [hideDrop, sendButtonClickAnalytics]);

    const hasAvailableSettings = availableSettings?.length > 0;
    if (!hasAvailableSettings || !activeSettings || !isAllowedUser) {
        return null;
    }

    const switchToggled = switchActivity !== filtersOnTheRight?.value;
    const content = (
        <Form ref={formElement} data-qa="resume-serp__view-form">
            <VSpacingContainer default={16}>
                {filtersOnTheRight && !isMobile && (
                    <>
                        <input type="hidden" name={`cs-${filtersOnTheRight.name}`} value={String(switchToggled)} />
                        <input type="hidden" name={filtersOnTheRight.name} value={String(switchActivity)} />
                        <Cell align="center" right={<Switch checked={switchActivity} onClick={toggleSwitch} />}>
                            <CellText>{trls[TrlKeys.settingName[filtersOnTheRight.name]]}</CellText>
                        </Cell>
                    </>
                )}
                {availableSettings.length !== 0 &&
                    availableSettings.map((field) => (
                        <Cell
                            key={field}
                            align="top"
                            left={
                                <Checkbox
                                    name="field"
                                    defaultChecked={activeSettings.includes(field)}
                                    value={field}
                                    data-qa={`resume-serp__view-${field}`}
                                />
                            }
                        >
                            <CellText maxLines={3}>{trls[TrlKeys.settingType[field]]}</CellText>
                        </Cell>
                    ))}
            </VSpacingContainer>
        </Form>
    );

    const saveButton = (
        <Button
            style="accent"
            mode="primary"
            onClick={handleSaveForm}
            loading={isFetching}
            data-qa="resume-view__save-button"
            stretched
        >
            {trls[TrlKeys.save]}
        </Button>
    );

    return (
        <>
            <ElementShownAnchor fn={sendElementShownAnalytics}>
                <div ref={activatorRef}>
                    <Button
                        style="neutral"
                        mode="secondary"
                        onClick={handleDropOpen}
                        size="small"
                        icon={<ListOutlinedSize16 />}
                        badge={showViewBadge ? <Badge size="extra-small" /> : undefined}
                        data-qa="resume-view__settings-button"
                    >
                        {trls[TrlKeys.view]}
                    </Button>
                </div>
            </ElementShownAnchor>
            <Drop
                visible={isDropVisible}
                placement="bottom-right"
                activatorRef={activatorRef}
                onClose={handleClose}
                width={390}
                minHeight={400}
                ignoreIntersections
                forcePlacement
                footer={saveButton}
            >
                {content}
            </Drop>
            <BottomSheet
                visible={isDropVisible}
                onClose={handleClose}
                footer={<BottomSheetFooter>{saveButton}</BottomSheetFooter>}
            >
                {content}
            </BottomSheet>
        </>
    );
};

export default translation(ResumeView);
